// Public directory of helpdesks — /p/helpdesks.
// Monochrome, ItemList JSON-LD, links into each public center.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchKbDirectory, type PublicKbDirectoryEntry } from '@/lib/kb-public';
import { setDocumentMeta, setCanonical, injectJsonLdList, buildItemListSchema } from '@/lib/seo';
import { IS_PRERENDER, useClientReady, readSnapshot, HydrationSnapshot, stableDate } from '@/lib/hydration';

// Hydration snapshot (see lib/hydration) so the first client render matches
// the prerendered list instead of starting empty.
const DIR_SNAPSHOT_ID = 'hcdir-snapshot';
const DIR_SNAPSHOT_KEY = 'directory';

export function HelpCentersDirectoryPage() {
  const ready = useClientReady();
  const [seed] = useState(() => readSnapshot<{ key: string; centers: PublicKbDirectoryEntry[] }>(DIR_SNAPSHOT_ID, DIR_SNAPSHOT_KEY));
  const [centers, setCenters] = useState<PublicKbDirectoryEntry[] | null>(seed?.centers ?? null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchKbDirectory()
      .then((r) => setCenters(r.centers))
      .catch(() => { if (!seed) setError(true); });
  }, [seed]);

  useEffect(() => {
    document.querySelectorAll('script[type="application/ld+json"]').forEach((n) => n.remove());
    const title = 'Helpdesks powered by Kora';
    const desc = 'Browse self-service helpdesks running on Kora — guides, answers and troubleshooting from real teams.';
    setDocumentMeta(title, desc);
    setCanonical('/p/helpdesks');
    return injectJsonLdList([
      buildItemListSchema(
        (centers ?? []).map((c) => ({
          name: c.title,
          url: `https://usekora.app/p/helpdesk/${c.ident}`,
          description: c.intro || `${c.article_count} articles`,
        })),
      ),
    ]);
  }, [centers]);

  return (
    <div className="min-h-screen bg-white text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <p className="text-xs font-semibold uppercase tracking-widest text-zinc-400">usekora.app</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">Helpdesks powered by Kora</h1>
        <p className="mt-2 max-w-lg text-[15px] text-zinc-500 dark:text-zinc-400">
          Self-service answers from teams that ship. Every center below runs on Kora's pay-as-you-go helpdesk.
        </p>

        {centers !== null && (IS_PRERENDER || seed) && (
          <HydrationSnapshot id={DIR_SNAPSHOT_ID} data={seed ?? { key: DIR_SNAPSHOT_KEY, centers }} />
        )}
        {error && <p className="mt-10 text-sm text-zinc-400">Couldn't load the directory right now.</p>}
        {centers !== null && centers.length === 0 && (
          <p className="mt-10 text-sm text-zinc-400">No public helpdesks yet — the first ones are being written.</p>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {(centers ?? []).map((c) => (
            <Link key={c.workspace_id} to={`/p/helpdesk/${c.ident}`}
              className="group flex gap-4 rounded-2xl border border-zinc-200 p-4 transition-colors hover:border-zinc-400 dark:border-zinc-800 dark:hover:border-zinc-600">
              {c.logo_url ? (
                <img src={c.logo_url} alt={c.name} className="size-10 shrink-0 rounded-lg object-cover" />
              ) : (
                <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-sm font-bold text-zinc-500 dark:bg-zinc-800">
                  {c.name.slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-[15px] font-semibold group-hover:underline group-hover:underline-offset-2">{c.title}</span>
                {c.intro && <span className="mt-0.5 line-clamp-2 block text-[13px] text-zinc-500 dark:text-zinc-400">{c.intro}</span>}
                <span className="mt-1 block text-xs text-zinc-400">
                  {c.article_count} article{c.article_count === 1 ? '' : 's'}
                  {c.latest_published_at ? ` · updated ${ready ? new Date(c.latest_published_at).toLocaleDateString() : stableDate(c.latest_published_at)}` : ''}
                </span>
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-zinc-200 p-6 text-center dark:border-zinc-800">
          <p className="text-sm font-semibold">Want one for your team?</p>
          <p className="mx-auto mt-1 max-w-sm text-[13px] text-zinc-500 dark:text-zinc-400">
            A branded help-center blog with analytics — 1 credit per published article, edits free.
          </p>
          <a href="https://usekora.app/auth"
            className="mt-4 inline-block rounded-lg bg-zinc-900 px-4 py-2 text-sm font-semibold text-white hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-300">
            Create your helpdesk
          </a>
        </div>
      </div>
    </div>
  );
}
